import { Injectable } from '@angular/core';

import { Table } from '../models/table';

/**
 * Fonte única dos valores apresentados ao usuário e enviados ao back no fluxo de pagamento.
 *
 * Em TOTAL, back é fonte da verdade: `table.remaining` já vem com voucher/fidelity/
 * cashback/paid abatidos (node-api updateCart + qrcode-client.service ajusta voucher).
 *
 * Em PARTIAL/CUSTOM, acréscimo % (table.tax) e descontos % (voucher/fidelity) são
 * calculados proporcionalmente em cima do que o usuário vai pagar — back ainda não
 * conhece a fatia que vai virar pagamento, então a regra de 3 fica no front.
 *
 * Consumidores:
 * - select-payment: exibição (Subtotal/Total/Restante) e mount do wallet
 * - table-values-summary-list: linhas do resumo + Total
 * - payment.service.executePayment: totalPaid do intent
 */
@Injectable({ providedIn: 'root' })
export class PaymentValuesService {
  /** Arredondamento monetário pra 2 casas — evita 3+ decimais em multiplicações %. */
  private round2(n: number): number {
    return Math.round((Number(n) || 0) * 100) / 100;
  }

  /** Cashback aplicado na mesa, vindo do back (user_table_cashback.cashback_value). */
  getCashbackApplied(table: Table): number {
    return this.round2(Number(table?.cashbackApplied) || 0);
  }

  /**
   * Subtotal exibido — itens "limpos" (sem taxa de serviço/acréscimo).
   * Em PARTIAL devolve só os itens regulares selecionados (sem o tip prop e sem o
   * proportionalTax), pra que Subtotal + linhas de Acréscimo/Taxa de serviço fechem
   * visualmente até o Total no resumo.
   */
  getSubtotal(table: Table): number {
    if (table.paymentType === 'TOTAL') return this.round2(table.subtotal);
    if (table.paymentType === 'PARTIAL') return this.round2(table.getTotalItensSelectedWithoutTip());
    return this.round2(table.customPayment ?? 0);
  }

  /**
   * Base pra regra de 3 dos descontos % em PARTIAL/CUSTOM.
   * Regra: todo desconto % incide sobre `subtotal (produtos) + taxa de serviço`.
   * Taxa de serviço é "produto" (line item SERVICE) — entra na base.
   * Acréscimo automático (tax) NÃO entra — bate com a regra do back para voucher
   * (`cart.total * pct / 100`, onde cart.total já inclui serviceFee mas não tax).
   * CUSTOM: valor digitado + serviceFee proporcional (espelha PARTIAL).
   * TOTAL: callers usam discountValueApplied (voucher) direto do back ou helper
   * próprio (fidelity TOTAL), não chama essa função.
   */
  private getDiscountBase(table: Table): number {
    if (table.paymentType === 'PARTIAL' || table.paymentType === 'CUSTOM') {
      const subtotal = table.paymentType === 'PARTIAL'
        ? table.getTotalItensSelectedWithoutTip()
        : (Number(table.customPayment) || 0);
      return subtotal + this.getProportionalServiceFee(table);
    }
    return Number(table.subtotal) || 0;
  }

  /**
   * Acréscimo (auto-incremento %) que vai pesar neste pagamento.
   * PARTIAL: lê o `proportionalTax` que o getter `totalItensSelected` calcula.
   * CUSTOM: regra de 3 em cima do valor digitado.
   * TOTAL: valor cheio da mesa.
   */
  getProportionalTax(table: Table): number {
    if (table.paymentType === 'TOTAL') return this.round2(Number(table.tax) || 0);
    if (table.paymentType === 'PARTIAL') {
      // side-effect: setta table.proportionalTax
      void table.totalItensSelected;
      return this.round2(Number(table.proportionalTax) || 0);
    }
    if (!table.tax || !table.total) return 0;
    return this.round2(
      ((Number(table.customPayment) || 0) / Number(table.total)) * Number(table.tax),
    );
  }

  /**
   * Taxa de serviço (line item com `tip=true`, i.e. "Taxa de serviço") que pesa
   * neste pagamento. Em PARTIAL/CUSTOM, regra de 3 sobre os itens regulares; em
   * TOTAL, valor cheio que o back devolve em `serviceFeeValue`.
   */
  getProportionalServiceFee(table: Table): number {
    if (table.paymentType === 'TOTAL') return this.round2(Number(table.serviceFeeValue) || 0);
    const tip = table.getTip();
    if (!tip?.value) return 0;
    const denom = (Number(table.total) || 0) - tip.value - table.getServiceItemsValue();
    if (denom <= 0) return 0;
    const userBase = table.paymentType === 'PARTIAL'
      ? table.getTotalItensSelectedWithoutTip()
      : (Number(table.customPayment) || 0);
    return this.round2(tip.value * (userBase / denom));
  }

  /**
   * Desconto do cupom que pesa neste pagamento.
   * PERCENTAGE em PARTIAL/CUSTOM: aplica % sobre a base do usuário.
   * VALUE: 0 em PARTIAL/CUSTOM (how-to-pay.hasFixedModifiers bloqueia PARTIAL nesse caso).
   * TOTAL: discountValueApplied cheio do back.
   */
  getProportionalVoucherDiscount(table: Table): number {
    const v = table.vouchers?.[0];
    if (!v) return 0;
    if (table.paymentType === 'TOTAL') return this.round2(Number(v.discountValueApplied) || 0);
    if (v.discountType !== 'PERCENTAGE') return 0;
    return this.round2(
      (this.getDiscountBase(table) * (Number(v.discountValue) || 0)) / 100,
    );
  }

  /** Desconto da fidelidade que pesa neste pagamento. Mesma lógica do voucher. */
  getProportionalFidelityDiscount(table: Table): number {
    const f = table.fidelity;
    if (!f) return 0;
    if (table.paymentType === 'TOTAL') {
      if (f.discountType === 'VALUE') return this.round2(Number(f.discountValue) || 0);
      // Regra: fidelity % aplica DEPOIS de TODOS os outros descontos+acréscimo
      // (manual + cupom + acréscimo + cashback). Base = subtotal + serviceFee +
      // tax - manual - voucher - cashback. Bate com o back: `(cart.total + tax -
      // discount - voucher - cashback) * pct / 100`.
      const voucher = this.round2(Number(table.vouchers?.[0]?.discountValueApplied) || 0);
      const discount = this.round2(Number(table.discount) || 0);
      const cashback = this.getCashbackApplied(table);
      const base = Math.max(
        0,
        (Number(table.subtotal) || 0)
          + (Number(table.serviceFeeValue) || 0)
          + (Number(table.tax) || 0)
          - discount
          - voucher
          - cashback,
      );
      return this.round2((base * (Number(f.discountValue) || 0)) / 100);
    }
    if (f.discountType !== 'PERCENTAGE') return 0;
    // PARTIAL/CUSTOM: fidelity proporcional. Calcula o fidelity_total da mesa
    // (subtotal + serviceFee + tax - manual - cupom - cashback) × pct, depois
    // aplica a fração que o usuário vai pagar.
    const cartTotal = (Number(table.subtotal) || 0) + (Number(table.serviceFeeValue) || 0);
    if (cartTotal <= 0) return 0;
    const tax = Number(table.tax) || 0;
    const discount = Number(table.discount) || 0;
    const voucher = this.round2(Number(table.vouchers?.[0]?.discountValueApplied) || 0);
    const cashback = this.getCashbackApplied(table);
    const fidelityBaseFull = Math.max(0, cartTotal + tax - discount - voucher - cashback);
    const fidelityTotal = (fidelityBaseFull * (Number(f.discountValue) || 0)) / 100;
    const userShare = this.getDiscountBase(table) / cartTotal;
    return this.round2(fidelityTotal * userShare);
  }

  /**
   * Total da mesa cheia com TODOS os descontos/acréscimos aplicados —
   * independente de paymentType. Espelha o `displayTotal` em TOTAL: parte
   * do `tableFinalValue` (já com voucher e cashback abatidos pelo nest) e
   * abate a fidelidade local (back não desconta no DTO). Usado pra calcular
   * o cashback gerado ("Você vai ganhar X de cashback nesta compra"), que
   * sempre incide no total da mesa, não no que o usuário vai pagar agora.
   */
  getTableFinalTotal(table: Table): number {
    const tableFinalValue = Number(table?.tableFinalValue) || 0;
    const f = table?.fidelity;
    let fidelityDiscount = 0;
    if (f) {
      if (f.discountType === 'VALUE') {
        fidelityDiscount = Number(f.discountValue) || 0;
      } else if (f.discountType === 'PERCENTAGE') {
        fidelityDiscount = (tableFinalValue * (Number(f.discountValue) || 0)) / 100;
      }
    }
    return this.round2(Math.max(0, tableFinalValue - fidelityDiscount));
  }

  /** Valor final cobrado do usuário neste pagamento (com todos os abatimentos). */
  getTotalToPay(table: Table): number {
    const cashback = this.getCashbackApplied(table);
    if (table.paymentType === 'PARTIAL') {
      const voucher = this.getProportionalVoucherDiscount(table);
      const fidelity = this.getProportionalFidelityDiscount(table);
      return this.round2(
        Math.max(0, table.totalItensSelected - voucher - fidelity - cashback),
      );
    }
    if (table.paymentType === 'CUSTOM') {
      // CUSTOM: cobra exatamente o valor digitado. Não soma tax/serviceFee nem
      // abate voucher/fidelity/cashback — descontos da mesa pesam em quem paga
      // o restante (ou no remaining via back).
      return this.round2(Math.max(0, Number(table.customPayment) || 0));
    }
    return this.round2(Math.max(0, table.remaining ?? 0));
  }

  /** Restante na mesa após este pagamento (lado UI). */
  getRemainingAfterPayment(table: Table): number {
    if (table.paymentType === 'TOTAL') return 0;
    return this.round2(Math.max(0, (table.remaining ?? 0) - this.getTotalToPay(table)));
  }
}
